import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Progress, Select, Space, Tag, Tooltip, Typography, notification } from 'antd';
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../assets/utils/lib';

const { Text } = Typography;

const money = (v) => (
  <CurrencyFormat value={Number(v || 0)} prefix="₦" thousandSeparator="," displayType="text" decimalScale={2} />
);

/**
 * One consumption row: "consumed of limit", with what unfiled lines would add
 * shown as pending, and remaining after them. No limit → spend only.
 */
/**
 * A value in the limit's unit (the pool metric): money in naira; sessions /
 * visits / times, days and units as counts. The cost of a service is never
 * the consumption figure for a count- or day-limited benefit.
 */
const UNIT = { count: ['time', 'times'], days: ['day', 'days'], quantity: ['unit', 'units'] };
const fmtMeasure = (metric, v) => {
  if (!metric || metric === 'money') return money(v);
  const n = Number(v || 0);
  const [one, many] = UNIT[metric] || UNIT.count;
  return `${n.toLocaleString('en-NG', { maximumFractionDigits: 2 })} ${n === 1 ? one : many}`;
};

function PositionRow({ label, badge, metric = 'money', limit, limitText, consumed, proposed, remaining, remainingAfter, breach, hint }) {
  const isText = metric === 'text';
  const hasLimit = !isText && limit !== null && limit !== undefined;
  const lim = Number(limit || 0);
  const con = Number(consumed || 0);
  const pend = Number(proposed || 0);
  const after = con + pend;
  const pct = hasLimit && lim > 0 ? Math.min(100, Math.round((after / lim) * 100)) : 0;
  const color = breach ? '#ff4d4f' : pct > 85 ? '#faad14' : '#49B870';
  const fmt = (v) => fmtMeasure(metric, v);
  const verb = metric === 'money' ? 'spent' : 'used';
  return (
    <div style={{ padding: '6px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
        <span>
          {badge}
          <Text strong>{label}</Text>
          {hint ? <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>{hint}</Text> : null}
        </span>
        {isText ? (
          <Text type="secondary">{limitText || 'Limit described in the plan'}</Text>
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {fmt(con)}
            {pend > 0 ? <Text type="secondary"> + {fmt(pend)} pending → <b>{fmt(after)}</b></Text> : null}
            {hasLimit ? <> {verb} of {fmt(lim)}</> : <Text type="secondary"> {verb} · no limit</Text>}
          </span>
        )}
      </div>
      {hasLimit ? (
        <>
          <Progress percent={pct} strokeColor={color} showInfo={false} size="small" style={{ margin: '2px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: breach ? '#ff4d4f' : '#888' }}>
            <span>Remaining: {fmt(pend > 0 && remainingAfter !== null && remainingAfter !== undefined ? remainingAfter : remaining)}{pend > 0 ? ' after pending' : ''}</span>
            <span>{breach ? 'Over limit' : `${pct}% used`}</span>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * Benefit Item mapping with live consumption, for every place work happens:
 * the encounter filing form (unfiled lines by name), the PA page and every
 * claims interface (saved lines by check-in code).
 *
 *   * Envelope: the client's consumption of the selected check-in type.
 *   * Lines: each line's Benefit Item — recorded, resolved by the global name
 *     mapping, or the single covered candidate — with a picker when a decision
 *     is needed. Mapping is by item NAME and applies everywhere at once.
 *   * Benefit items: the client's spend on each touched Benefit Item against
 *     its applicable limit, with unfiled lines shown as pending.
 *
 * Numbers are read live from claim lines, so a mapping made here (or on the
 * Benefit Item page) changes the figures immediately. Silent when the API is
 * unreachable, the viewer lacks the permission, or the client has no plan.
 *
 * Props: `checkinCode` for saved lines; or `insuranceNo` + `checkinTypeId` +
 * `lines` ([{ key, item_name, category_id, amount, quantity }]) for a form.
 */
export default function PmMappingPanel({
  checkinCode,
  insuranceNo,
  checkinTypeId,
  lines: unfiledLines,
  source = 'claims',
  title = 'Benefit mapping & consumption',
  refreshKey = 0,
  onContext,
  onMapped,
  // PA page: envelope, then every benefit's consumption, then the requested
  // items and their mapping, so utilisation is visible without scrolling.
  consumptionFirst = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [choices, setChoices] = useState({});
  const [saving, setSaving] = useState(null);
  const timer = useRef(null);

  const linesSignature = useMemo(() => JSON.stringify((unfiledLines || []).map((l) => [l.key, l.item_name, l.category_id, l.amount, l.quantity])), [unfiledLines]);

  const load = useCallback(async () => {
    if (!checkinCode && !insuranceNo) { setData(null); return; }
    setLoading(true);
    const payload = await lib.pmConsumptionContext({
      checkin_code: checkinCode || undefined,
      insurance_no: insuranceNo || undefined,
      checkin_type_id: checkinTypeId || undefined,
      lines: (unfiledLines || []).map((l) => ({
        key: String(l.key),
        item_name: l.item_name,
        category_id: l.category_id,
        amount: Number(l.amount || 0),
        quantity: Number(l.quantity || 1),
        benefit_item_id: choices[String(l.key)] || undefined,
      })),
    });
    setData(payload);
    // The host can show what the panel resolved (plan name from the policy record).
    if (typeof onContext === 'function') onContext(payload);
    setLoading(false);
  // linesSignature stands in for the lines array (a new array identity every render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkinCode, insuranceNo, checkinTypeId, linesSignature, refreshKey]);

  // Debounced: the filing form changes lines and amounts keystroke by keystroke.
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(load, 250);
    return () => clearTimeout(timer.current);
  }, [load]);

  if (!data || !data.available) return null;
  const lines = data.lines || [];
  const hasAnything = data.envelope || lines.length > 0 || (data.benefit_items || []).length > 0;
  if (!hasAnything) return null;

  const map = async (line) => {
    const benefitId = choices[line.key];
    if (!benefitId) return;
    setSaving(line.key);
    const payload = { benefit_item_id: benefitId, source };
    if (line.saved && line.claim_item_id) payload.claim_item_ids = [line.claim_item_id];
    if (line.item_name) payload.item_names = [line.item_name];
    const res = await lib.pmMap(payload);
    setSaving(null);
    if (res?.error) {
      notification.error({ message: res.message || 'Mapping failed', placement: 'topLeft' });
      return;
    }
    notification.success({ message: `Mapped "${line.item_name}"`, description: res?.message, placement: 'topLeft' });
    if (typeof onMapped === 'function') onMapped(); else load();
  };

  const needsDecision = lines.filter((l) => l.status === 'mapping_required' || l.status === 'unmapped');
  const mappedCount = lines.filter((l) => l.status === 'mapped').length;
  const catalogById = {};
  (data.catalog || []).forEach((c) => { catalogById[c.id] = c.name; });

  return (
    <div className="pm-mapping-panel" style={{ marginTop: 12, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}>
      <Space align="center" style={{ marginBottom: 4 }} wrap>
        <LinkOutlined />
        <Text strong style={{ fontSize: 13 }}>{title}</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {data.plan_name ? `${data.plan_name} · ` : ''}
          {lines.length ? `${mappedCount} of ${lines.length} item${lines.length === 1 ? '' : 's'} mapped` : 'no items yet'}
          {needsDecision.length ? ` · ${needsDecision.length} need${needsDecision.length === 1 ? 's' : ''} a Benefit Item` : ''}
        </Text>
        <Button size="small" type="text" icon={<ReloadOutlined />} loading={loading} onClick={load} />
      </Space>

      {data.envelope ? (
        <PositionRow
          label={`${data.envelope.name || data.checkin_type_name || 'Envelope'} envelope`}
          hint={data.envelope.source === 'legacy' ? 'plan limit' : null}
          metric={data.envelope.metric || 'money'}
          limitText={data.envelope.limit_text}
          limit={data.envelope.limit}
          consumed={data.envelope.consumed}
          proposed={data.envelope.proposed}
          remaining={data.envelope.remaining}
          remainingAfter={data.envelope.remaining_after_proposed}
          breach={data.envelope.would_breach}
        />
      ) : data.checkin_type_name ? (
        <div style={{ fontSize: 12, padding: '4px 0' }}><Text type="secondary">No envelope limit is defined for {data.checkin_type_name} on this plan.</Text></div>
      ) : null}

      {consumptionFirst ? (
        <>
      {(data.benefit_items || []).length ? (
        <div style={{ marginTop: 4 }}>
          {data.benefit_items.map((bi) => (
            <PositionRow
              key={bi.benefit_item_id}
              label={bi.name}
              badge={bi.shared_pool ? <Tooltip title={`Shares the "${bi.pool_name}" limit with other benefit items; remaining is the shared balance.`}><Tag style={{ marginRight: 6 }}>shared limit</Tag></Tooltip> : null}
              hint={bi.state === 'uncapped' ? 'covered, no cap' : bi.state === 'not_covered' ? 'not on this plan' : null}
              metric={bi.metric || 'money'}
              limitText={bi.limit_text}
              limit={bi.limit}
              consumed={bi.consumed}
              proposed={bi.proposed}
              remaining={bi.remaining}
              remainingAfter={bi.remaining_after_proposed}
              breach={bi.would_breach}
            />
          ))}
        </div>
      ) : null}
          {lines.length ? (
            <div style={{ borderTop: '1px solid #e8e8e8', marginTop: 8, paddingTop: 6 }}>
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Requested items and mapping
              </Text>
      {lines.map((line, ix) => {
        const isMapped = line.status === 'mapped';
        const options = (line.candidates || []).map((c) => ({ value: c.id, label: c.name, title: c.name }));
        return (
          <div key={line.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 12 }}>
              <Text strong={!isMapped}>{line.item_name || (line.claim_item_id ? `line #${line.claim_item_id}` : 'item')}</Text>
              <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>{money(line.amount)}{line.saved ? '' : ' · not filed yet'}</Text>
              <div style={{ fontSize: 11 }}>
                {isMapped ? (
                  <>
                    <Tag color="green" style={{ marginInlineEnd: 4, whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '100%' }}>→ {line.benefit_item_name}</Tag>
                    <Text type="secondary">
                      {line.via === 'auto' ? 'same cover — no mapping needed' : line.via === 'name' || line.via === 'name_mapping' ? 'via name mapping' : line.via === 'chosen' ? 'selected here' : 'mapped'}
                    </Text>
                  </>
                ) : line.status === 'not_coverable' ? (
                  <Tag color="red" style={{ marginInlineEnd: 0 }}>Not covered on this plan for this check-in type</Tag>
                ) : (
                  <Tag color="gold" style={{ marginInlineEnd: 0 }}>Unmapped — choose a Benefit Item</Tag>
                )}
              </div>
            </div>
            {line.status !== 'not_coverable' && (options.length > 0 || isMapped) ? (
              // Plain flex row, not Space.Compact: the compact group sizes to its
              // content, so a long benefit name pushed the Map button out of view.
              // Here the select shrinks (and ellipsises) and the button never does.
              <div style={{ display: 'flex', gap: 4, flex: '1 1 260px', minWidth: 0, maxWidth: '100%' }}>
                <Select
                  size="small"
                  style={{ flex: '1 1 0', minWidth: 0 }}
                  placeholder={isMapped ? 'Change Benefit Item…' : 'Map to Benefit Item…'}
                  value={choices[line.key]}
                  onChange={(v) => setChoices((c) => ({ ...c, [line.key]: v }))}
                  showSearch
                  optionFilterProp="label"
                  popupMatchSelectWidth={false}
                  dropdownStyle={{ minWidth: 320, maxWidth: 'min(520px, 90vw)' }}
                  optionRender={(opt) => (
                    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{opt.label}</span>
                  )}
                  options={options.length ? options : (data.catalog || []).map((c) => ({ value: c.id, label: c.name, title: c.name }))}
                />
                <Tooltip title={isMapped ? 'Re-map this name everywhere' : 'Map this name everywhere; consumption updates at once'}>
                  <Button
                    size="small"
                    style={{ flex: '0 0 auto' }}
                    type={isMapped ? 'default' : 'primary'}
                    loading={saving === line.key}
                    disabled={!choices[line.key]}
                    onClick={() => map(line)}
                  >
                    {isMapped ? 'Re-map' : 'Map'}
                  </Button>
                </Tooltip>
              </div>
            ) : null}
          </div>
        );
      })}

            </div>
          ) : null}
        </>
      ) : (
        <>
      {lines.map((line, ix) => {
        const isMapped = line.status === 'mapped';
        const options = (line.candidates || []).map((c) => ({ value: c.id, label: c.name, title: c.name }));
        return (
          <div key={line.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 12 }}>
              <Text strong={!isMapped}>{line.item_name || (line.claim_item_id ? `line #${line.claim_item_id}` : 'item')}</Text>
              <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>{money(line.amount)}{line.saved ? '' : ' · not filed yet'}</Text>
              <div style={{ fontSize: 11 }}>
                {isMapped ? (
                  <>
                    <Tag color="green" style={{ marginInlineEnd: 4, whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '100%' }}>→ {line.benefit_item_name}</Tag>
                    <Text type="secondary">
                      {line.via === 'auto' ? 'same cover — no mapping needed' : line.via === 'name' || line.via === 'name_mapping' ? 'via name mapping' : line.via === 'chosen' ? 'selected here' : 'mapped'}
                    </Text>
                  </>
                ) : line.status === 'not_coverable' ? (
                  <Tag color="red" style={{ marginInlineEnd: 0 }}>Not covered on this plan for this check-in type</Tag>
                ) : (
                  <Tag color="gold" style={{ marginInlineEnd: 0 }}>Unmapped — choose a Benefit Item</Tag>
                )}
              </div>
            </div>
            {line.status !== 'not_coverable' && (options.length > 0 || isMapped) ? (
              // Plain flex row, not Space.Compact: the compact group sizes to its
              // content, so a long benefit name pushed the Map button out of view.
              // Here the select shrinks (and ellipsises) and the button never does.
              <div style={{ display: 'flex', gap: 4, flex: '1 1 260px', minWidth: 0, maxWidth: '100%' }}>
                <Select
                  size="small"
                  style={{ flex: '1 1 0', minWidth: 0 }}
                  placeholder={isMapped ? 'Change Benefit Item…' : 'Map to Benefit Item…'}
                  value={choices[line.key]}
                  onChange={(v) => setChoices((c) => ({ ...c, [line.key]: v }))}
                  showSearch
                  optionFilterProp="label"
                  popupMatchSelectWidth={false}
                  dropdownStyle={{ minWidth: 320, maxWidth: 'min(520px, 90vw)' }}
                  optionRender={(opt) => (
                    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{opt.label}</span>
                  )}
                  options={options.length ? options : (data.catalog || []).map((c) => ({ value: c.id, label: c.name, title: c.name }))}
                />
                <Tooltip title={isMapped ? 'Re-map this name everywhere' : 'Map this name everywhere; consumption updates at once'}>
                  <Button
                    size="small"
                    style={{ flex: '0 0 auto' }}
                    type={isMapped ? 'default' : 'primary'}
                    loading={saving === line.key}
                    disabled={!choices[line.key]}
                    onClick={() => map(line)}
                  >
                    {isMapped ? 'Re-map' : 'Map'}
                  </Button>
                </Tooltip>
              </div>
            ) : null}
          </div>
        );
      })}

      {(data.benefit_items || []).length ? (
        <div style={{ borderTop: '1px solid #e8e8e8', marginTop: 4 }}>
          {data.benefit_items.map((bi) => (
            <PositionRow
              key={bi.benefit_item_id}
              label={bi.name}
              badge={bi.shared_pool ? <Tooltip title={`Shares the "${bi.pool_name}" limit with other benefit items; remaining is the shared balance.`}><Tag style={{ marginRight: 6 }}>shared limit</Tag></Tooltip> : null}
              hint={bi.state === 'uncapped' ? 'covered, no cap' : bi.state === 'not_covered' ? 'not on this plan' : null}
              metric={bi.metric || 'money'}
              limitText={bi.limit_text}
              limit={bi.limit}
              consumed={bi.consumed}
              proposed={bi.proposed}
              remaining={bi.remaining}
              remainingAfter={bi.remaining_after_proposed}
              breach={bi.would_breach}
            />
          ))}
        </div>
      ) : null}
        </>
      )}
    </div>
  );
}
